import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LogoMark from './LogoMark';
import { LoadingOverlayModal } from './LoadingOverlayModal';
import { sessionManager } from '@/services/sessionManager';

interface InitialLoadingScreenProps {
  children: React.ReactNode;
}

/**
 * InitialLoadingScreen - covers flickers during app initialization
 * Shows loader until authentication state is determined
 */
export const InitialLoadingScreen: React.FC<InitialLoadingScreenProps> = ({
  children,
}) => {
  const { status, hasAuthenticated } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isReauthRequired, setIsReauthRequired] = useState(false);

  useEffect(() => {
    // If status is no longer loading, initialization is complete
    if (status !== 'loading') {
      setIsInitialized(true);
    }
  }, [status]);

  useEffect(() => {
    const unsubscribeRequired = sessionManager.on('reauth-required', () => {
      setIsReauthRequired(true);
    });
    const unsubscribeSuccess = sessionManager.on('reauth-success', () => {
      setIsReauthRequired(false);
    });
    const unsubscribeCancelled = sessionManager.on('reauth-cancelled', () => {
      setIsReauthRequired(false);
    });

    return () => {
      unsubscribeRequired();
      unsubscribeSuccess();
      unsubscribeCancelled();
    };
  }, []);

  const shouldShowLoader =
    (!isInitialized || status === 'loading') && !isReauthRequired && hasAuthenticated;

  return (
    <>
      {children}
      {shouldShowLoader && (
        <>
          <LoadingOverlayModal />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'var(--gray-3)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
            }}
          >
            <div
              style={{
                animation: 'logoFadeIn 0.8s ease-out',
                transformOrigin: 'center',
                fontSize: '2rem',
              }}
            >
              <LogoMark />
            </div>
          </div>
        </>
      )}
    </>
  );
};
